"use client";

import { useMemo } from "react";
import { Bot } from "lucide-react";
import Link from "next/link";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChatRef, CostInfo } from "@/lib/hooks/use-chat-stream";

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  refs?: ChatRef[];
  isStreaming?: boolean;
  cost?: CostInfo | null;
  ragContext?: string | null;
  parsedQuery?: Record<string, string[]> | null;
}

/**
 * 스트리밍 중 마크다운 기호 처리:
 * - 완성된 줄(개행으로 끝난)은 마크다운 렌더링
 * - 마지막 미완성 줄에서 닫히지 않은 마크다운 기호는 숨김
 * - 스트리밍 완료 후 전체를 마크다운으로 렌더링
 */
function splitForStreaming(content: string): { markdown: string; plainTail: string } {
  const lastNL = content.lastIndexOf("\n");
  if (lastNL === -1) {
    // 아직 줄바꿈 없음 — 전부 미완성
    return { markdown: "", plainTail: stripUnclosed(content) };
  }
  const completedLines = content.slice(0, lastNL + 1);
  const tail = content.slice(lastNL + 1);
  return { markdown: completedLines, plainTail: stripUnclosed(tail) };
}

/** 미완성 줄에서 닫히지 않은 마크다운 기호를 제거 */
function stripUnclosed(text: string): string {
  if (!text) return "";

  // 줄 시작의 미완성 헤딩 기호 (#, ##, ### 뒤에 내용 없음)
  const headingOnly = text.match(/^#{1,6}\s*$/);
  if (headingOnly) return "";

  // 줄 시작의 완성된 헤딩 기호는 유지 (# 텍스트)
  let result = text;

  // 닫히지 않은 볼드/이탤릭 (끝에 남은 *, **, ***)
  // 열린 ** 또는 * 가 닫히지 않았으면 제거
  result = removeUnclosedInline(result, "***");
  result = removeUnclosedInline(result, "**");
  result = removeUnclosedInline(result, "*");
  result = removeUnclosedInline(result, "~~");
  result = removeUnclosedInline(result, "`");

  // 줄 끝에 남은 미완성 링크 기호 [, [텍스트, [텍스트](
  result = result.replace(/\[[^\]]*$/, "");
  result = result.replace(/\]\([^)]*$/, "");

  return result;
}

/** 인라인 마커가 홀수(열림만, 닫히지 않음)면 마지막 열린 것부터 제거 */
function removeUnclosedInline(text: string, marker: string): string {
  let count = 0;
  let idx = 0;
  let lastOpenIdx = -1;
  while (idx < text.length) {
    if (text.startsWith(marker, idx)) {
      count++;
      if (count % 2 === 1) lastOpenIdx = idx;
      idx += marker.length;
    } else {
      idx++;
    }
  }
  // 홀수 = 닫히지 않음 → 마지막 열린 위치부터 삭제
  if (count % 2 === 1 && lastOpenIdx >= 0) {
    return text.slice(0, lastOpenIdx);
  }
  return text;
}

export function ChatMessage({
  role,
  content,
  refs,
  isStreaming,
  cost,
  ragContext,
  parsedQuery,
}: ChatMessageProps) {
  const { markdown, plainTail } = useMemo(
    () => (isStreaming ? splitForStreaming(content) : { markdown: content, plainTail: "" }),
    [content, isStreaming],
  );

  if (role === "user") {
    return (
      <div className="flex justify-end px-4 py-2" data-role="user">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-violet-600 px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap text-white">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2.5 px-4 py-2">
      <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-50">
        <Bot className="h-3.5 w-3.5 text-violet-600" strokeWidth={2} />
      </div>
      <div className="max-w-[85%] space-y-2">
        <div className="rounded-2xl rounded-bl-md border border-gray-100 bg-gray-50 px-4 py-3 text-sm leading-relaxed text-gray-700">
          {markdown && (
            <div className="chat-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-violet-600 underline underline-offset-2 transition-colors hover:text-violet-700"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {markdown}
              </ReactMarkdown>
            </div>
          )}
          {plainTail && <span>{plainTail}</span>}
          {isStreaming && (
            <span className="ml-1 inline-flex gap-[2px] align-middle">
              <span
                className="streaming-dot h-[5px] w-[5px] rounded-full"
                style={{ animationDelay: "0s" }}
              />
              <span
                className="streaming-dot h-[5px] w-[5px] rounded-full"
                style={{ animationDelay: "0.15s" }}
              />
              <span
                className="streaming-dot h-[5px] w-[5px] rounded-full"
                style={{ animationDelay: "0.3s" }}
              />
            </span>
          )}
        </div>
        {!isStreaming && refs && refs.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-1">
            {refs.map((ref) => (
              <Link
                key={ref.slug}
                href={`/wiki/${ref.slug}`}
                className="inline-flex items-center gap-1 rounded-full border border-violet-100 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-100"
              >
                {ref.title}
              </Link>
            ))}
          </div>
        )}
        {!isStreaming && cost && (
          <div className="mt-1 flex items-center gap-2 px-1 text-[10px] text-gray-400">
            <span>{cost.model}</span>
            <span>·</span>
            <span>{cost.total_tokens.toLocaleString()} 토큰</span>
            <span>·</span>
            <span className="font-medium text-gray-500">{cost.cost_krw}</span>
            <span className="text-gray-300">({cost.cost_usd})</span>
          </div>
        )}
        {!isStreaming && (ragContext || parsedQuery) && (
          <details className="mt-1 px-1">
            <summary className="cursor-pointer text-[10px] text-gray-400 select-none hover:text-gray-500">
              RAG 컨텍스트 보기
            </summary>
            {parsedQuery && (
              <div className="mt-1 rounded-lg border border-violet-100 bg-violet-50 p-2 text-xs text-violet-600">
                <span className="font-medium">질문 파싱:</span>{" "}
                {Object.entries(parsedQuery)
                  .filter(([, v]) => v && v.length > 0)
                  .map(([k, v]) => `${k}=[${v.join(", ")}]`)
                  .join(" · ")}
              </div>
            )}
            {ragContext && (
              <pre className="mt-1 max-h-60 overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 bg-gray-100 p-3 text-xs whitespace-pre-wrap text-gray-500">
                {ragContext}
              </pre>
            )}
          </details>
        )}
      </div>
    </div>
  );
}
